import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  ListSimulationsForSystemResponse,
  SimulationRun_State,
  SimulationRun_Type,
} from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import { TFunction } from 'i18next';
import { SimulationModel } from '~/shared/models/system/SimulationModel';
import { assertIsDefined } from '~/types/assert-type';

export interface HistoricalSimulationGroup {
  type: SimulationRun_Type.HISTORICAL;
  timeFrom: Date;
  timeTo: Date;
  simulations: SimulationModel[];
  title: (t: TFunction) => string;
}

export interface IdfSimulationGroup {
  type: SimulationRun_Type.IDF;
  idfConfigKey: string;
  idfPrimarySubtitle: (t: TFunction) => string;
  idfSecondarySubtitle: (t: TFunction) => string;
  simulations: SimulationModel[];
  title: (t: TFunction) => string;
}

export type SimulationGroup = HistoricalSimulationGroup | IdfSimulationGroup;

export class SystemSimulationsListModel {
  readonly simulations: SimulationModel[];

  readonly simulationGroups: SimulationGroup[];

  readonly allFinished: boolean;

  constructor(
    listSimulationsForSystemResponse: ListSimulationsForSystemResponse,
  ) {
    this.simulations = listSimulationsForSystemResponse.simulations.map(
      (simulation) => new SimulationModel(simulation),
    );

    this.allFinished = this.simulations.every(
      (simulation) =>
        simulation.state === SimulationRun_State.DONE ||
        simulation.state === SimulationRun_State.FAILED,
    );

    this.simulationGroups = SystemSimulationsListModel.buildSimulationGroups(
      this.simulations,
    );
  }

  static getGroupKey(group: SimulationGroup): string {
    if (group.type === SimulationRun_Type.HISTORICAL) {
      return `${group.timeFrom.getTime()}-${group.timeTo.getTime()}`;
    }
    return `idf-${group.idfConfigKey}`;
  }

  private static buildSimulationGroups(
    simulations: SimulationModel[],
  ): SimulationGroup[] {
    const groupsMap = new Map<string, SimulationGroup>();

    simulations.forEach((simulation) => {
      if (simulation.type === SimulationRun_Type.HISTORICAL) {
        SystemSimulationsListModel.addToHistoricalGroup(groupsMap, simulation);
      }
      if (simulation.type === SimulationRun_Type.IDF) {
        SystemSimulationsListModel.addToIdfGroup(groupsMap, simulation);
      }
    });

    return Array.from(groupsMap.values());
  }

  private static addToHistoricalGroup(
    groupsMap: Map<string, SimulationGroup>,
    simulation: SimulationModel,
  ): void {
    const timeFrom = timestampDate(simulation.timeFrom);
    const timeTo = timestampDate(simulation.timeTo);
    const historicalKey = SystemSimulationsListModel.createTimeRangeKey(
      timeFrom,
      timeTo,
    );
    const groupKey = `historical-${historicalKey}`;

    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        type: SimulationRun_Type.HISTORICAL,
        timeFrom,
        timeTo,
        simulations: [],
        title: (t: TFunction) =>
          `${t('simulations.type.historical')} — ${timeFrom.toLocaleDateString()} - ${timeTo.toLocaleDateString()}`,
      });
    }

    groupsMap.get(groupKey)!.simulations.push(simulation);
  }

  private static addToIdfGroup(
    groupsMap: Map<string, SimulationGroup>,
    simulation: SimulationModel,
  ): void {
    const configKey = simulation.getIdfConfigKey() ?? simulation.id;
    const groupKey = `idf-${configKey}`;

    if (!groupsMap.has(groupKey)) {
      const parameters = simulation.getIdfReportParameters();
      assertIsDefined(parameters);

      const {
        durationMinutes,
        intensity,
        fillPercentageStart,
        timeBeforeEventHours,
        timeAfterEventHours,
        systemMode,
      } = parameters;

      groupsMap.set(groupKey, {
        type: SimulationRun_Type.IDF,
        idfConfigKey: configKey,
        idfPrimarySubtitle: (t: TFunction) =>
          [
            `${t('simulations.createSimulation.formFields.duration')}: ${durationMinutes} ${t('simulations.createSimulation.formFields.durationUnit')}`,
            `${t('simulations.createSimulation.formFields.intensity')}: ${intensity} ${t('simulations.createSimulation.formFields.intensityUnit')}`,
          ].join(' · '),

        idfSecondarySubtitle: (t: TFunction) =>
          [
            `${t('simulations.createSimulation.formFields.fillPercentageStart')}: ${fillPercentageStart} ${t('simulations.createSimulation.formFields.fillPercentageStartUnit')}`,
            `${t('simulations.createSimulation.formFields.timeBeforeEventHours')}: ${timeBeforeEventHours} ${t('simulations.createSimulation.formFields.hoursUnit')}`,
            `${t('simulations.createSimulation.formFields.timeAfterEventHours')}: ${timeAfterEventHours} ${t('simulations.createSimulation.formFields.hoursUnit')}`,
            `${t('simulations.createSimulation.formFields.systemMode')}: ${t(
              systemMode === 'blue'
                ? 'simulations.createSimulation.formFields.systemModeBlue'
                : 'simulations.createSimulation.formFields.systemModeGreen',
            )}`,
          ].join(' · '),

        simulations: [],

        title: (t: TFunction) => t('simulations.type.idf'),
      });
    }
    groupsMap.get(groupKey)!.simulations.push(simulation);
  }

  private static createTimeRangeKey(timeFrom: Date, timeTo: Date): string {
    return `${timeFrom.getTime()}-${timeTo.getTime()}`;
  }
}
